/**
 * card-variant.js
 * ------------------------------------------------------------------
 * Pure variant-resolution logic for a product card. No DOM access, no
 * network calls — just product JSON in, resolved variant/availability
 * out. This isolation is what made the original color/size desync bug
 * possible to find and fix in one place instead of three.
 *
 * Usage:
 *   const resolver = new CardVariantResolver(productData);
 *   resolver.setOption('Color', 'Blue');
 *   resolver.setOption('Size', 'Medium');
 *   resolver.currentVariant // -> matching variant object or null
 *
 * Exposed globally as window.CardVariantResolver (no bundler in this
 * theme, so plain script-tag globals are used throughout the module
 * set — see card-product.js for load order).
 */
(function () {
  class CardVariantResolver {
    constructor(productData) {
      this.productData = productData || null;
      this.options = (productData && productData.options) || [];
      this.selectedOptions = {};
      this.currentVariant = null;
    }

    /**
     * Replace the product data wholesale (e.g. if the card is reused
     * for a different product) and re-resolve.
     */
    setProductData(productData) {
      this.productData = productData;
      this.options = (productData && productData.options) || [];
      this._resolve();
      return this.currentVariant;
    }

    /**
     * Set a single option's selected value and re-resolve the variant.
     * This is the ONLY way selectedOptions should be mutated — keeping
     * every write funneled through here is what prevents the state
     * from drifting out of sync with what's selected on screen.
     */
    setOption(name, value) {
      if (value == null) {
        delete this.selectedOptions[name];
      } else {
        this.selectedOptions[name] = value;
      }
      this._resolve();
      return this.currentVariant;
    }

    /**
     * Bulk-seed initial state (e.g. from server-rendered "selected"
     * attributes) without triggering N separate resolves.
     */
    seedOptions(optionMap) {
      this.selectedOptions = { ...optionMap };
      this._resolve();
      return this.currentVariant;
    }

    _resolve() {
      if (!this.productData) {
        this.currentVariant = null;
        return;
      }
      const options = this.options;
      this.currentVariant =
        this.productData.variants.find((v) =>
          options.every((name, i) => {
            const chosen = this.selectedOptions[name];
            return chosen == null || v[`option${i + 1}`] === chosen;
          })
        ) || null;
    }

    /**
     * Is `value` for option `optionName` purchasable given every OTHER
     * currently-selected option? Used to disable/enable size chips
     * (or any option chips) relative to the rest of the selection.
     */
    isValueAvailable(optionName, value) {
      if (!this.productData) return false;
      const options = this.options;
      const index = options.indexOf(optionName);
      if (index < 0) return false;

      return this.productData.variants.some((v) => {
        const matchesOtherOptions = options.every((name, i) => {
          if (name === optionName) return true;
          const chosen = this.selectedOptions[name];
          return chosen == null || v[`option${i + 1}`] === chosen;
        });
        return matchesOtherOptions && v[`option${index + 1}`] === value && v.available;
      });
    }

    /**
     * Returns { value: boolean } availability map for every value of
     * `optionName` that appears anywhere in the product's variants,
     * given the currently-selected OTHER options.
     */
    getAvailabilityMap(optionName) {
      if (!this.productData) return {};
      const index = this.options.indexOf(optionName);
      if (index < 0) return {};

      const values = new Set(this.productData.variants.map((v) => v[`option${index + 1}`]));
      const map = {};
      values.forEach((value) => {
        map[value] = this.isValueAvailable(optionName, value);
      });
      return map;
    }

    /**
     * Re-validate `optionName`'s current selection against the rest of
     * the selection (e.g. after a color change) and, if it's no longer
     * available, fall back to the first available value — optionally
     * restricted to `candidateValues` (e.g. only values that actually
     * have a rendered chip on this card).
     *
     * Returns the resolved value (which may be unchanged, swapped, or
     * null if nothing is available at all), and re-resolves
     * currentVariant accordingly.
     */
    resyncOption(optionName, candidateValues) {
      const current = this.selectedOptions[optionName];
      if (current != null && this.isValueAvailable(optionName, current)) {
        this._resolve();
        return current;
      }

      const pool = candidateValues && candidateValues.length
        ? candidateValues
        : Object.keys(this.getAvailabilityMap(optionName));

      const fallback = pool.find((value) => this.isValueAvailable(optionName, value));

      if (fallback != null) {
        this.selectedOptions[optionName] = fallback;
      } else {
        delete this.selectedOptions[optionName];
      }

      this._resolve();
      return fallback ?? null;
    }
  }

  window.CardVariantResolver = CardVariantResolver;
})();
